import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const HydraFacial = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const hydraFacialTypes = [
    {
      title: 'Signature HydraFacial',
      subtitle: '$150 OR PER SESSION',
      details: 'SHOW MORE DETAILS',
      packageOffers: [
        '3 Sessions: $405',
        '6 Sessions: $675 (5% off per session)',
        '8 Sessions: $1,000'
      ]
    },
    {
      title: 'Deluxe HydraFacial',
      subtitle: '$200 OR PER SESSION',
      details: 'SHOW MORE DETAILS',
      packageOffers: [
        '3 Sessions: $540',
        '6 Sessions: $1075 (5% off per session)',
        '8 Sessions: $1,350'
      ]
    },
    {
      title: 'Platinum HydraFacial',
      subtitle: '$275 OR PER SESSION',
      details: 'SHOW MORE DETAILS',
      packageOffers: [
        '3 Sessions: $742',
        '6 Sessions: $1477 (5% off per session)',
        '8 Sessions: $1,850'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Deep Cleansing',
      description: 'Thoroughly cleanses and removes impurities, dead skin cells, and excess oil from deep within pores.'
    },
    {
      title: 'Exfoliation and Hydration',
      description: 'Gently exfoliates while simultaneously delivering intense hydration for refreshed, glowing skin.'
    },
    {
      title: 'Improves Skin Texture',
      description: 'Smooths rough patches and refines skin texture for a more even, polished complexion.'
    },
    {
      title: 'Reduces Fine Lines',
      description: 'Minimizes the appearance of fine lines and wrinkles through deep hydration and nourishment.'
    },
    {
      title: 'Instant Results',
      description: 'See immediate improvements in skin clarity, brightness, and overall radiance after just one treatment.'
    },
    {
      title: 'Safe for All Skin Types',
      description: 'Suitable for all skin types and tones, including sensitive skin, with customizable options.'
    },
    {
      title: 'No Downtime',
      description: 'Non-invasive treatment with zero recovery time - return to your daily activities immediately.'
    },
    {
      title: 'Addresses Multiple Concerns',
      description: 'Treats various skin issues including congestion, dullness, uneven tone, and enlarged pores.'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-28">
     

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/hydr.png" 
                  alt="HydraFacial Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                HYDRAFACIAL TREATMENT FOR RADIANT, GLOWING SKIN
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  HydraFacial is a revolutionary 3-in-1 facial treatment that cleanses, exfoliates, and hydrates skin using patented vortex technology. This non-invasive procedure delivers instant results with zero downtime, making it perfect for all skin types including sensitive skin. Available in three customizable tiers—Signature, Deluxe, and Platinum—each HydraFacial treatment targets specific concerns like fine lines, acne, hyperpigmentation, enlarged pores, and uneven texture while infusing skin with nourishing serums for immediate radiance.
                </p>
                <p>
                  Experience plumper, more hydrated skin immediately after your first session, with continued improvements in texture, tone, and clarity. For optimal results and lasting skin health, monthly HydraFacial treatments are recommended. Book your HydraFacial at Century Longevity & Aesthetics in Forest Hills, NY, and discover the #1 facial treatment trusted worldwide for glowing, healthy skin.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">HydraFacial Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {hydraFacialTypes.map((type, index) => (
              <HydraFacialCard key={type.title} type={type} index={index} scrollToContact={scrollToContact} />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Carousel Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of HydraFacial</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Experience the ultimate facial treatment with HydraFacial. Achieve clearer, more radiant, and hydrated skin with immediate results!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about HydraFacial treatments, what to expect during your appointment, and how we can help you achieve glowing skin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('hydrafacial');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// HydraFacialCard Component
const HydraFacialCard = ({ type, index, scrollToContact }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="bg-white rounded-lg p-8 shadow-xl"
    >
      <div className="text-center mb-6">
        <h3 className="text-2xl font-heading font-semibold text-olive-darkest mb-2">{type.title}</h3>
        <p className="text-lg font-body text-gold font-medium mb-4">{type.subtitle}</p>
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs font-semibold text-olive-darkest hover:text-gold transition-colors duration-300 uppercase mb-6"
        >
          {type.details}
        </button>
      </div>

      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 pt-4 border-t border-gray-200"
        >
          <h4 className="text-sm font-semibold text-olive-darkest mb-3 uppercase">Package Offers:</h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {type.packageOffers.map((offer, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-gold mt-1">•</span>
                <span>{offer}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      <motion.button
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={scrollToContact}
        className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
      >
        Book Now
      </motion.button>
    </motion.div>
  );
};

export default HydraFacial;